/**
 * Rental Vehicle List Page
 * Epic 34: Járműnyilvántartás (ADR-027)
 * Bérgép járművek: utánfutók, aggregátorok
 */

import type { RentalVehicleStatus, RentalVehicleType } from '@/api/vehicles';
import { useRentalVehicles, useVehicleMutations } from '@/hooks/use-vehicles';
import { useTranslation } from '@kgc/i18n';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  Eye,
  Filter,
  Pencil,
  Plus,
  Search,
  Trash2,
  Truck,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Vehicle types
type VehicleType = RentalVehicleType;
type VehicleStatus = RentalVehicleStatus;

const TYPE_LABEL_KEYS: Record<VehicleType, string> = {
  trailer: 'vehicle.typeLabels.trailer',
  aggregator: 'vehicle.typeLabels.aggregator',
  platform: 'vehicle.typeLabels.platform',
  other: 'vehicle.typeLabels.other',
};

function isExpiringSoon(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return date <= thirtyDaysFromNow && date > now;
}

function isExpired(dateStr: string): boolean {
  return new Date(dateStr) <= new Date();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function RentalVehicleListPage() {
  const { t } = useTranslation();

  const STATUS_CONFIG: Record<
    VehicleStatus,
    { label: string; color: string; icon: typeof CheckCircle }
  > = {
    active: {
      label: t('vehicle.statusLabels.available'),
      color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      icon: CheckCircle,
    },
    rented: {
      label: t('status.rentedOut'),
      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      icon: Clock,
    },
    maintenance: {
      label: t('vehicle.statusLabels.maintenance'),
      color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      icon: AlertTriangle,
    },
    inactive: {
      label: t('vehicle.statusLabels.inactive'),
      color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      icon: XCircle,
    },
  };
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<VehicleType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | 'all'>('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Interim ceiling (BE-max=100, Javo-ratify): a client-side filter a teljes halmazon kell
  // működjön → load-all-at-100. A flotta tipikusan ≤100 → teljesen fixelt (data-loss 20→100).
  const { vehicles, isLoading, error, refetch } = useRentalVehicles({ pageSize: 100 });
  const { deleteVehicle } = useVehicleMutations();

  // Filter vehicles client-side
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      const matchesSearch =
        searchQuery === '' ||
        vehicle.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === 'all' || vehicle.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [vehicles, searchQuery, typeFilter, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = vehicles.length;
    const active = vehicles.filter(v => v.status === 'active').length;
    const rented = vehicles.filter(v => v.status === 'rented').length;
    const expiringSoon = vehicles.filter(
      v =>
        isExpiringSoon(v.technicalExpiryDate) ||
        isExpiringSoon(v.registrationExpiryDate) ||
        isExpiringSoon(v.insuranceExpiryDate)
    ).length;
    return { total, active, rented, expiringSoon };
  }, [vehicles]);

  const handleDelete = async (id: string, name: string) => {
    if (confirm(t('vehicle.deleteConfirm', { name }))) {
      try {
        await deleteVehicle(id);
        void refetch();
      } catch {
        alert(t('vehicle.deleteFailed'));
      }
    }
  };

  return (
    <div className="kgc-bg min-h-screen p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('vehicle.rentalVehicles')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('vehicle.subtitle')}</p>
        </div>
        <Link
          to="/vehicles/rental/new"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus className="h-5 w-5" />
          {t('vehicle.newVehicle')}
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <Truck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('vehicle.totalVehicles')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('vehicle.available')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('vehicle.rentedOut')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.rented}</p>
            </div>
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/30">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('vehicle.expiringIn30Days')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.expiringSoon}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('vehicle.searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
          />
        </div>
        <div className="flex gap-2">
          {/* Type Filter */}
          <div className="relative">
            <button
              onClick={() => {
                setShowTypeDropdown(!showTypeDropdown);
                setShowStatusDropdown(false);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Filter className="h-4 w-4" />
              {typeFilter === 'all' ? t('common.type') : t(TYPE_LABEL_KEYS[typeFilter])}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showTypeDropdown && (
              <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                <button
                  onClick={() => {
                    setTypeFilter('all');
                    setShowTypeDropdown(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {t('vehicle.allTypes')}
                </button>
                {Object.entries(TYPE_LABEL_KEYS).map(([key, labelKey]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setTypeFilter(key as VehicleType);
                      setShowTypeDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative">
            <button
              onClick={() => {
                setShowStatusDropdown(!showStatusDropdown);
                setShowTypeDropdown(false);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {statusFilter === 'all' ? t('table.status') : STATUS_CONFIG[statusFilter].label}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showStatusDropdown && (
              <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setShowStatusDropdown(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {t('vehicle.allStatuses')}
                </button>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setStatusFilter(key as VehicleStatus);
                      setShowStatusDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading / Error state */}
      {isLoading && (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
          {t('vehicle.loadingVehicles')}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Data Table */}
      {!isLoading && (
        <div className="kgc-card-bg overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('vehicle.vehicleColumn')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('common.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('vehicle.technicalExpiry')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('vehicle.registrationExpiry')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('vehicle.insuranceExpiry')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('vehicle.operations')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {filteredVehicles.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                    >
                      {t('vehicle.noResults')}
                    </td>
                  </tr>
                ) : (
                  filteredVehicles.map(vehicle => {
                    const StatusIcon = STATUS_CONFIG[vehicle.status].icon;
                    return (
                      <tr key={vehicle.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="whitespace-nowrap px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {vehicle.licensePlate}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {vehicle.name} • {vehicle.brand} {vehicle.model}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                          {t(TYPE_LABEL_KEYS[vehicle.type] ?? vehicle.type)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CONFIG[vehicle.status].color}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {STATUS_CONFIG[vehicle.status].label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={
                              isExpired(vehicle.technicalExpiryDate)
                                ? 'font-medium text-red-600 dark:text-red-400'
                                : isExpiringSoon(vehicle.technicalExpiryDate)
                                  ? 'font-medium text-orange-600 dark:text-orange-400'
                                  : 'text-gray-700 dark:text-gray-300'
                            }
                          >
                            {formatDate(vehicle.technicalExpiryDate)}
                            {isExpired(vehicle.technicalExpiryDate) && (
                              <AlertTriangle className="ml-1 inline h-3 w-3" />
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={
                              isExpired(vehicle.registrationExpiryDate)
                                ? 'font-medium text-red-600 dark:text-red-400'
                                : isExpiringSoon(vehicle.registrationExpiryDate)
                                  ? 'font-medium text-orange-600 dark:text-orange-400'
                                  : 'text-gray-700 dark:text-gray-300'
                            }
                          >
                            {formatDate(vehicle.registrationExpiryDate)}
                            {isExpired(vehicle.registrationExpiryDate) && (
                              <AlertTriangle className="ml-1 inline h-3 w-3" />
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={
                              isExpired(vehicle.insuranceExpiryDate)
                                ? 'font-medium text-red-600 dark:text-red-400'
                                : isExpiringSoon(vehicle.insuranceExpiryDate)
                                  ? 'font-medium text-orange-600 dark:text-orange-400'
                                  : 'text-gray-700 dark:text-gray-300'
                            }
                          >
                            {formatDate(vehicle.insuranceExpiryDate)}
                            {isExpired(vehicle.insuranceExpiryDate) && (
                              <AlertTriangle className="ml-1 inline h-3 w-3" />
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/vehicles/rental/${vehicle.id}`)}
                              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                              title={t('common.details')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/vehicles/rental/${vehicle.id}/edit`)}
                              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                              title={t('common.edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(vehicle.id, vehicle.name)}
                              className="rounded p-1 text-gray-500 hover:bg-red-100 hover:text-red-700 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
